<template>
  <div class="contents">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-lg-12">
          <div class="breadcrumb-main user-member justify-content-sm-between">
            <div class="d-flex flex-wrap justify-content-center breadcrumb-main__wrapper">
              <h4 class="text-capitalize fw-500 breadcrumb-title">Generate Invoice User</h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Info Alert -->
      <div class="alert alert-info">
        <b>Cara pakai:</b>
        Masukkan email / username / nomor HP user, klik <b>Cari Akun</b>.
        Setelah data muncul, klik <b>Download Invoice PDF</b> untuk dapat file PDF siap dikirim.
        Nomor invoice & tanggal otomatis di-generate berdasarkan paket aktif user tersebut.
      </div>

      <!-- Form Cari Akun -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card p-4 mb-4 global-shadow border bg-white radius-xl">
            <h5 class="mb-3"><i class="fas fa-search text-primary"></i> Cari Akun</h5>
            <form @submit.prevent="lookupUser">
              <div class="row align-items-end">
                <div class="col-md-9 mb-2">
                  <label class="small mb-1">Email / Username / Nomor HP</label>
                  <input
                    v-model.trim="query"
                    type="text"
                    class="form-control"
                    placeholder="Contoh: edwardwidodo@gmail.com"
                    autofocus
                    required
                  />
                </div>
                <div class="col-md-3 mb-2">
                  <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading">
                    <i class="fas fa-search"></i>
                    {{ isLoading ? 'Mencari...' : 'Cari Akun' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Hasil Pencarian -->
      <div v-if="user" class="row">
        <div class="col-lg-12">
          <div class="card p-4 mb-4 global-shadow border bg-white radius-xl">
            <h5 class="mb-3"><i class="fas fa-user-circle text-success"></i> Data Akun Ditemukan</h5>

            <div class="row">
              <!-- User Info -->
              <div class="col-md-6">
                <div class="info-block">
                  <div class="info-label">DETAIL USER</div>
                  <table class="table-info">
                    <tr><td>User ID</td><td><b>#{{ user.id }}</b></td></tr>
                    <tr><td>Nama</td><td>{{ user.name || '-' }}</td></tr>
                    <tr><td>Email</td><td>{{ user.email || '-' }}</td></tr>
                    <tr><td>Username</td><td>{{ user.username || '-' }}</td></tr>
                    <tr><td>No. HP</td><td>{{ user.phone || '-' }}</td></tr>
                    <tr><td>Terdaftar Sejak</td><td>{{ formatDate(user.created_at) }}</td></tr>
                    <tr><td>Status</td>
                      <td>
                        <span v-if="user.status == 1 || user.status == '1'" class="badge badge-success">Aktif</span>
                        <span v-else class="badge badge-danger">Nonaktif</span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Package Info -->
              <div class="col-md-6">
                <div class="info-block">
                  <div class="info-label">PAKET AKTIF</div>
                  <div v-if="!packageMaster || !userPackage" class="text-muted">
                    <i class="fas fa-exclamation-triangle"></i>
                    User belum punya paket aktif.
                  </div>
                  <table v-else class="table-info">
                    <tr><td>Nama Paket</td><td><b>{{ packageMaster.name }}</b></td></tr>
                    <tr><td>Harga</td><td><b class="text-primary">{{ formatRupiah(effectivePackage.price) }}</b></td></tr>
                    <tr><td>Durasi</td><td>{{ packageMaster.duration }} hari</td></tr>
                    <tr><td>Berlaku Sampai</td><td>{{ formatDate(userPackage.expired_at) }}</td></tr>
                    <tr>
                      <td>Sisa Hari</td>
                      <td>
                        <span v-if="daysLeft >= 0" :class="daysLeft <= 7 ? 'badge badge-danger' : daysLeft <= 30 ? 'badge badge-warning text-white' : 'badge badge-info'">
                          {{ daysLeft }} hari lagi
                        </span>
                        <span v-else class="badge badge-danger">EXPIRED</span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <!-- Form Ganti Paket -->
            <hr class="mt-4">
            <h6 class="mb-3"><i class="fas fa-exchange-alt text-info"></i> Ganti Paket di Invoice (Opsional)</h6>
            <div class="alert alert-light border" style="font-size:13px;">
              <i class="fas fa-info-circle text-info"></i>
              Default invoice menggunakan <b>paket aktif user</b>. Kalau Kakak mau invoice ini untuk paket berbeda (misal cross-sell/upgrade), pilih paket lain di bawah ini.
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label class="small mb-1">Pilih Paket untuk Invoice</label>
                <select v-model="selectedPackageId" class="form-control form-control-sm">
                  <option :value="null">— Pakai paket aktif user (default) —</option>
                  <option
                    v-for="p in allPackages"
                    :key="p.id"
                    :value="p.id"
                  >
                    {{ p.name }} — {{ formatRupiah(p.price) }} ({{ p.duration }} hari)
                  </option>
                </select>
                <small v-if="loadingPackages" class="text-muted">Loading paket...</small>
                <small v-else class="text-muted">{{ allPackages.length }} paket tersedia</small>
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Harga Override (opsional)</label>
                <input v-model="customPrice" type="number" class="form-control form-control-sm" placeholder="kosong = pakai harga default" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="small mb-1">Durasi Override (hari, opsional)</label>
                <input v-model="customDuration" type="number" class="form-control form-control-sm" placeholder="kosong = pakai durasi default" />
              </div>
            </div>
            <div v-if="effectivePackage && (selectedPackageId || customPrice || customDuration)" class="alert alert-warning mt-2" style="font-size:13px;">
              <b>📋 Invoice akan menampilkan:</b> {{ effectivePackage.name }} — <b>{{ formatRupiah(effectivePackage.price) }}</b> ({{ effectivePackage.duration }} hari)
            </div>

            <!-- Form Custom Invoice -->
            <hr class="mt-4">
            <h6 class="mb-3"><i class="fas fa-edit text-warning"></i> Atur Invoice (Opsional)</h6>
            <div class="row">
              <div class="col-md-4 mb-2">
                <label class="small mb-1">Nomor Invoice</label>
                <input v-model="invoice.number" type="text" class="form-control form-control-sm" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="small mb-1">Tanggal Terbit</label>
                <input v-model="invoice.issueDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-md-4 mb-2">
                <label class="small mb-1">Jatuh Tempo</label>
                <input v-model="invoice.dueDate" type="date" class="form-control form-control-sm" />
              </div>
              <div class="col-md-12 mb-2">
                <label class="small mb-1">Status Pembayaran</label>
                <select v-model="invoice.status" class="form-control form-control-sm" style="max-width: 240px">
                  <option value="unpaid">Belum Dibayar</option>
                  <option value="paid">Lunas</option>
                </select>
              </div>
            </div>

            <hr class="mt-3">
            <div class="d-flex justify-content-between align-items-center mt-2">
              <small class="text-muted">
                <i class="fas fa-info-circle"></i>
                PDF akan otomatis ter-download dengan nama <code>invoice_{{ user.id }}_{{ slug(user.email) }}.pdf</code>
              </small>
              <button
                @click="generatePdf"
                class="btn btn-success"
                :disabled="!effectivePackage || isGenerating"
              >
                <i class="fas fa-file-pdf"></i>
                {{ isGenerating ? 'Membuat PDF...' : 'Download Invoice PDF' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- HIDDEN: PDF Render Target -->
      <vue-html2pdf
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="false"
        :paginate-elements-by-height="1400"
        :filename="pdfFileName"
        :pdf-quality="2"
        :manual-pagination="false"
        pdf-format="a4"
        pdf-orientation="portrait"
        pdf-content-width="800px"
        @hasGenerated="onPdfDone"
        ref="html2Pdf"
      >
        <section slot="pdf-content">
          <div class="invoice-pdf" v-if="user">
            <!-- HEADER -->
            <div class="inv-header">
              <div class="inv-header-left">
                <div class="inv-brand">StarSender</div>
                <div class="inv-tagline">WhatsApp Gateway &amp; Marketing Automation</div>
              </div>
              <div class="inv-header-right">
                <div class="inv-label">INVOICE</div>
                <div class="inv-number">{{ invoice.number }}</div>
                <div class="inv-label" style="margin-top: 6px;">Tanggal: {{ formatDateOnly(invoice.issueDate) }}</div>
              </div>
            </div>

            <!-- BILL TO + FROM -->
            <table class="inv-bill-table">
              <tr>
                <td class="inv-bill-cell">
                  <div class="inv-section-h">DITAGIHKAN KEPADA</div>
                  <div class="inv-name">{{ user.name || user.email }}</div>
                  <div class="inv-meta">User ID: #{{ user.id }}</div>
                  <div class="inv-meta">Email: {{ user.email }}</div>
                  <div class="inv-meta">Telepon: {{ user.phone || '-' }}</div>
                </td>
                <td class="inv-bill-cell">
                  <div class="inv-section-h">DARI</div>
                  <div class="inv-name">StarSender</div>
                  <div class="inv-meta inv-meta-small">WhatsApp Gateway &amp; Marketing Automation</div>
                  <div class="inv-meta">Email: starfield.id@gmail.com</div>
                  <div class="inv-meta">WhatsApp: +62 813-1922-7392</div>
                  <div class="inv-meta">Website: https://starsender.online</div>
                </td>
              </tr>
            </table>

            <!-- INFO BAR -->
            <table class="inv-info-bar">
              <tr>
                <td>
                  <div class="inv-info-label">Tanggal Terbit</div>
                  <div class="inv-info-value">{{ formatDateOnly(invoice.issueDate) }}</div>
                </td>
                <td>
                  <div class="inv-info-label">Jatuh Tempo</div>
                  <div class="inv-info-value">{{ formatDateOnly(invoice.dueDate) }}</div>
                </td>
                <td>
                  <div class="inv-info-label">Status</div>
                  <div class="inv-info-value">
                    <span v-if="invoice.status === 'paid'" style="color: #2e7d32;"><b>LUNAS</b></span>
                    <span v-else style="color: #d84315;"><b>BELUM DIBAYAR</b></span>
                  </div>
                </td>
              </tr>
            </table>

            <!-- ITEMS -->
            <table class="inv-items">
              <thead>
                <tr>
                  <th class="t-left">DESKRIPSI</th>
                  <th class="t-center" style="width: 60px;">QTY</th>
                  <th class="t-right" style="width: 130px;">HARGA</th>
                  <th class="t-right" style="width: 140px;">SUBTOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="effectivePackage">
                  <td>
                    <b>{{ effectivePackage.name }}</b><br>
                    <small style="color:#5a6a7c;">Periode: {{ formatDateOnly(userPackage && userPackage.created_at) }} - {{ formatDateOnly(userPackage && userPackage.expired_at) }} ({{ effectivePackage.duration }} hari)</small>
                  </td>
                  <td class="t-center">1</td>
                  <td class="t-right">{{ formatRupiah(effectivePackage.price) }}</td>
                  <td class="t-right"><b>{{ formatRupiah(effectivePackage.price) }}</b></td>
                </tr>
              </tbody>
            </table>

            <!-- TOTALS -->
            <table class="inv-totals">
              <tr>
                <td class="t-right" style="color: #5a6a7c;">Subtotal</td>
                <td class="t-right" style="color: #5a6a7c; width: 140px;">{{ formatRupiah(packageMaster ? packageMaster.price : 0) }}</td>
              </tr>
              <tr>
                <td class="t-right" style="color: #5a6a7c;">PPN (0%)</td>
                <td class="t-right" style="color: #5a6a7c;">Rp 0</td>
              </tr>
              <tr class="inv-total-row">
                <td class="t-right">TOTAL TAGIHAN</td>
                <td class="t-right">{{ formatRupiah(packageMaster ? packageMaster.price : 0) }}</td>
              </tr>
            </table>

            <!-- PAYMENT INFO -->
            <div class="inv-section-h" style="margin-top: 30px;">INFORMASI PEMBAYARAN</div>
            <div class="inv-payment-box">
              Silakan transfer ke salah satu rekening berikut, lalu konfirmasi pembayaran via
              WhatsApp dengan menyertakan bukti transfer:
              <br><br>
              <b>Bank BCA</b><br>
              No. Rekening: <b>7370673556</b><br>
              a/n Yohanes Santoso
              <br><br>
              <b>Bank Mandiri</b><br>
              No. Rekening: <b>1230005692423</b><br>
              a/n Yohanes Santoso
              <br><br>
              Konfirmasi pembayaran ke WhatsApp <b>+62 813-1922-7392</b>
              (Senin-Jumat 10:00-20:00 WIB) dengan menyertakan bukti transfer
              dan email akun Anda.
            </div>

            <!-- NOTES -->
            <div class="inv-section-h" style="margin-top: 24px;">CATATAN</div>
            <ul class="inv-notes">
              <li>Pembayaran wajib dilakukan sebelum tanggal jatuh tempo (<b>{{ formatDateOnly(invoice.dueDate) }}</b>) untuk menghindari pemutusan layanan.</li>
              <li>Setelah pembayaran terkonfirmasi, paket akan otomatis diperpanjang.</li>
              <li>Tidak ada refund untuk pembayaran yang sudah masuk.</li>
              <li>Jika ada pertanyaan, hubungi support kami di <b>+62 813-1922-7392</b> atau email <b>starfield.id@gmail.com</b>.</li>
            </ul>

            <!-- FOOTER -->
            <div class="inv-footer">
              Terima kasih sudah mempercayakan kebutuhan WhatsApp marketing Anda kepada StarSender.<br>
              <i>Invoice digenerate otomatis pada {{ formatDateOnly(invoice.issueDate) }} - <b>StarSender</b></i>
            </div>
          </div>
        </section>
      </vue-html2pdf>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueHtml2pdf from "vue-html2pdf";

export default {
  components: { VueHtml2pdf },
  data() {
    return {
      query: "",
      user: null,
      userPackage: null,
      packageMaster: null,
      isLoading: false,
      isGenerating: false,
      invoice: {
        number: "",
        issueDate: "",
        dueDate: "",
        status: "unpaid",
      },
      // === fitur Ganti Paket ===
      allPackages: [],          // list semua paket aktif
      selectedPackageId: null,  // ID paket yang dipilih admin (override packageMaster)
      customPrice: "",          // harga override (kosong = pakai harga paket default)
      customDuration: "",       // durasi override (hari, kosong = pakai durasi paket default)
      loadingPackages: false,
    };
  },
  computed: {
    daysLeft() {
      if (!this.userPackage || !this.userPackage.expired_at) return -1;
      const ms = parseInt(this.userPackage.expired_at);
      return Math.ceil((ms - Date.now()) / 86400000);
    },
    pdfFileName() {
      if (!this.user) return "invoice_starsender";
      return `invoice_${this.user.id}_${this.slug(this.user.email || this.user.username)}`;
    },
    // effectivePackage = paket yang dipakai untuk invoice (selected OR original)
    effectivePackage() {
      let base = this.packageMaster;
      if (this.selectedPackageId) {
        const found = this.allPackages.find(p => p.id == this.selectedPackageId);
        if (found) base = found;
      }
      if (!base) return null;
      // Clone + apply custom overrides
      const result = Object.assign({}, base);
      if (this.customPrice !== "" && !isNaN(parseInt(this.customPrice))) {
        result.price = parseInt(this.customPrice);
      }
      if (this.customDuration !== "" && !isNaN(parseInt(this.customDuration))) {
        result.duration = parseInt(this.customDuration);
      }
      return result;
    },
  },
  mounted() {
    this.loadAllPackages();
  },
  methods: {
    async loadAllPackages() {
      this.loadingPackages = true;
      try {
        // pakai endpoint /admin/packages (admin list semua paket) - kalau gak ada, fallback /packages (public)
        let resp;
        try {
          resp = await axios.get("admin/packages", { params: { limit: 500 } });
        } catch (e) {
          resp = await axios.get("packages", { params: { limit: 500 } });
        }
        const list = resp.data.data.packages || resp.data.data || [];
        // Filter hanya paket aktif/visible
        this.allPackages = Array.isArray(list) ? list : [];
      } catch (err) {
        console.error("Gagal load packages", err);
        this.allPackages = [];
      } finally {
        this.loadingPackages = false;
      }
    },
    formatRupiah(n) {
      if (n == null || n === "") return "Rp 0";
      const num = parseInt(n);
      if (isNaN(num)) return "Rp 0";
      return "Rp " + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    formatDate(millis) {
      if (!millis) return "-";
      return new Date(parseInt(millis)).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      }) + " WIB";
    },
    formatDateOnly(input) {
      if (!input) return "-";
      let d;
      if (typeof input === "string" && input.length === 10) {
        d = new Date(input + "T00:00:00");
      } else if (typeof input === "string" && input.includes("T")) {
        d = new Date(input);
      } else {
        d = new Date(parseInt(input));
      }
      if (isNaN(d.getTime())) return input;
      return d.toLocaleDateString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit", month: "long", year: "numeric",
      });
    },
    slug(s) {
      return (s || "user").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    },
    todayISO(offsetDays = 0) {
      const d = new Date();
      d.setDate(d.getDate() + offsetDays);
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    async lookupUser() {
      if (!this.query) return;
      this.isLoading = true;
      this.user = null;
      this.userPackage = null;
      this.packageMaster = null;
      try {
        const resp = await axios.get("admin/invoice-lookup", {
          params: { query: this.query },
        });
        this.user = resp.data.data.user;
        this.userPackage = resp.data.data.user_package;
        this.packageMaster = resp.data.data.package;
        // Auto-fill invoice fields
        const today = this.todayISO();
        const due = this.todayISO(7);
        this.invoice.number = `INV-${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${this.user.id}`;
        this.invoice.issueDate = today;
        this.invoice.dueDate = due;
        this.invoice.status = "unpaid";
        if (!this.packageMaster) {
          this.$toast.warning("User belum punya paket aktif. Tidak bisa generate invoice.");
        } else {
          this.$toast.success(`Akun ditemukan: ${this.user.email}`);
        }
      } catch (err) {
        const msg = err.response?.data?.message || "Gagal cari akun";
        this.$toast.error(msg);
      } finally {
        this.isLoading = false;
      }
    },
    generatePdf() {
      if (!this.effectivePackage) {
        this.$toast.error("Tidak ada paket dipilih");
        return;
      }
      this.isGenerating = true;
      this.$nextTick(() => {
        this.$refs.html2Pdf.generatePdf();
      });
    },
    onPdfDone() {
      this.isGenerating = false;
      this.$toast.success("Invoice PDF berhasil di-download");
    },
  },
};
</script>

<style scoped>
.info-block {
  background: #f8fafd;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  padding: 16px 18px;
  height: 100%;
}
.info-label {
  font-size: 11px;
  font-weight: 700;
  color: #5a6a7c;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  border-bottom: 1px solid #d6e4ff;
  padding-bottom: 6px;
}
.table-info {
  width: 100%;
  font-size: 13px;
}
.table-info td {
  padding: 4px 0;
}
.table-info td:first-child {
  color: #6c7a89;
  width: 38%;
}

/* INVOICE PDF STYLES */
.invoice-pdf {
  font-family: Helvetica, Arial, sans-serif;
  color: #1e2e3e;
  padding: 30px 40px;
  font-size: 12px;
  line-height: 1.5;
}
.inv-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  color: #fff;
  padding: 22px 28px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  border-radius: 6px;
}
.inv-brand {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.inv-tagline {
  font-size: 11px;
  opacity: 0.95;
  margin-top: 4px;
}
.inv-header-right {
  text-align: right;
}
.inv-label {
  font-size: 11px;
  opacity: 0.95;
  letter-spacing: 1px;
}
.inv-number {
  font-size: 18px;
  font-weight: 700;
  margin-top: 2px;
}
.inv-bill-table {
  width: 100%;
  margin-bottom: 18px;
}
.inv-bill-cell {
  width: 50%;
  vertical-align: top;
  padding-right: 18px;
}
.inv-section-h {
  font-size: 10px;
  font-weight: 700;
  color: #5a6a7c;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}
.inv-name {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
}
.inv-meta {
  font-size: 12px;
  color: #2c3e50;
  margin-bottom: 2px;
}
.inv-meta-small {
  font-size: 11px;
  color: #5a6a7c;
}
.inv-info-bar {
  width: 100%;
  background: #f6faff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  margin-bottom: 18px;
}
.inv-info-bar td {
  padding: 12px 14px;
  width: 33.33%;
  vertical-align: top;
  border-right: 1px solid #cfd8dc;
}
.inv-info-bar td:last-child {
  border-right: none;
}
.inv-info-label {
  font-size: 10px;
  color: #5a6a7c;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.inv-info-value {
  font-size: 12px;
  margin-top: 4px;
}
.inv-items {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  border: 1px solid #cfd8dc;
}
.inv-items thead th {
  background: #1976d2;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.5px;
  padding: 10px 12px;
  font-weight: 700;
}
.inv-items tbody td {
  padding: 10px 12px;
  border-top: 1px solid #e0e0e0;
  vertical-align: top;
}
.inv-totals {
  width: 100%;
  margin-bottom: 18px;
}
.inv-totals td {
  padding: 6px 12px;
  font-size: 12px;
}
.inv-totals tr.inv-total-row td {
  font-size: 14px;
  font-weight: 700;
  color: #0d47a1;
  background: #f6faff;
  border-top: 2px solid #1976d2;
  padding: 10px 12px;
}
.t-left { text-align: left; }
.t-center { text-align: center; }
.t-right { text-align: right; }
.inv-payment-box {
  background: #f6faff;
  border: 1px solid #cfd8dc;
  border-left: 3px solid #1976d2;
  border-radius: 6px;
  padding: 14px 18px;
  font-size: 12px;
  margin-bottom: 4px;
}
.inv-notes {
  font-size: 11px;
  color: #5a6a7c;
  padding-left: 18px;
  margin-bottom: 0;
}
.inv-notes li {
  margin-bottom: 3px;
  line-height: 1.5;
}
.inv-footer {
  font-size: 10px;
  color: #5a6a7c;
  text-align: center;
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
  margin-top: 28px;
}
</style>
